import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { api, packageEndpoints } from '../../api';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table } from '../../ui';
import { INPUT, dollars, inZone } from '../event/shared';
import { dateWords } from '../../terms';

/**
 * Importing season packages sold in the previous system (D-228).
 *
 * The same rule as the single-performance import beside it: **you read the dry
 * run before anything is written**, and the import button does not exist until
 * a preview has come back clean. A preview and an import send exactly the same
 * body to two endpoints, so what is on screen is what happens.
 *
 * The seat file covers **one** night. A season table is the same table every
 * night, so the night you name is the one whose tables the file describes, and
 * the rest of the package follows from it. Without that the seats could not be
 * placed at all, because the export carries no seat for the other nights.
 */

interface PackagePreview {
  package: { code: string; name: string; nights: number };
  orders: { buyer: string; email: string; table: string; seats: number; total_cents: number; comp: boolean; already_imported?: string }[];
  dropped: { line: number; label: string; status: string; attendee: string }[];
  problems: { code: string; message: string; line?: number }[];
  totals: { orders: number; tickets: number; total_cents: number; already_imported: number };
  ready: boolean;
}

interface PackageResult {
  import_id: string;
  imported: { order_number: string; buyer: string; table: string; seats: number; total_cents: number }[];
  skipped: { buyer: string; table: string; message: string }[];
  failures: { buyer: string; message: string }[];
  totals: { orders: number; tickets: number; total_cents: number };
}

const readFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });

export function PackageImportPanel() {
  const [packageId, setPackageId] = useState('');
  const [anchorId, setAnchorId] = useState('');
  const [seatCsv, setSeatCsv] = useState<{ name: string; text: string } | null>(null);
  const [buyerCsv, setBuyerCsv] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<PackagePreview | null>(null);
  const [result, setResult] = useState<PackageResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const packages = useQuery({ queryKey: ['packages'], queryFn: packageEndpoints.list });
  const chosen = packages.data?.packages.find((p) => p.id === packageId) ?? null;
  const nights = (chosen?.components ?? []).filter((k) => k.performance_id);

  const body = () => ({ package_code: chosen?.code ?? '', anchor_performance_id: anchorId, seat_csv: seatCsv?.text ?? '', buyer_csv: buyerCsv?.text ?? '' });

  const runPreview = useMutation({
    mutationFn: () => api.post<PackagePreview>('/api/admin/migration/packages/preview', body()),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      setError(null);
    },
    onError: (cause: ApiError) => {
      setPreview(null);
      setError(cause.message);
    },
  });

  const runImport = useMutation({
    mutationFn: () => api.post<PackageResult>('/api/admin/migration/packages/import', body()),
    onSuccess: (data) => {
      setResult(data);
      setPreview(null);
      setError(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const busy = runPreview.isPending || runImport.isPending;
  const canPreview = Boolean(chosen && anchorId && seatCsv && buyerCsv);

  // Any change to what would be sent throws the preview away: a dry run of one
  // thing must never authorize the import of another.
  const pick = (set: (value: { name: string; text: string } | null) => void) => async (file: File | undefined) => {
    setPreview(null);
    setResult(null);
    if (!file) return set(null);
    try {
      set({ name: file.name, text: await readFile(file) });
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  return (
    <Panel
      title="Season packages"
      description="Two files from the previous system: the seat list for one night, and the buyer export with the money. The table each buyer holds on that night is the table they keep all season."
    >
      <div className="space-y-4 px-5 py-4 text-sm">
        {error ? <ErrorNote>{error}</ErrorNote> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-ink-500 uppercase">Package</span>
            <select
              value={packageId}
              onChange={(e) => {
                setPackageId(e.target.value);
                setAnchorId('');
                setPreview(null);
                setResult(null);
              }}
              className={INPUT}
            >
              <option value="">Choose a package</option>
              {(packages.data?.packages ?? [])
                .filter((p) => p.package_kind === 'TABLE_SUBSCRIPTION' || p.package_kind === 'SEASON_TICKET')
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.components.length} night{p.components.length === 1 ? '' : 's'}
                  </option>
                ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink-500 uppercase">Night the seat file covers</span>
            <select
              value={anchorId}
              onChange={(e) => {
                setAnchorId(e.target.value);
                setPreview(null);
                setResult(null);
              }}
              disabled={!chosen}
              className={INPUT}
            >
              <option value="">Choose {dateWords().a}</option>
              {nights.map((k) => (
                <option key={k.id} value={k.performance_id!}>
                  {k.event_title} · {k.performance_starts_at ? inZone(k.performance_starts_at, k.timezone ?? 'America/Chicago') : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-ink-500 uppercase">Seat file (one night)</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => void pick(setSeatCsv)(e.target.files?.[0])} className="mt-1 w-full text-xs" />
            {seatCsv ? <span className="text-xs text-ink-500">{seatCsv.name}</span> : null}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink-500 uppercase">Buyer file (the money)</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => void pick(setBuyerCsv)(e.target.files?.[0])} className="mt-1 w-full text-xs" />
            {buyerCsv ? <span className="text-xs text-ink-500">{buyerCsv.name}</span> : null}
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={!canPreview || busy} onClick={() => runPreview.mutate()}>
            {runPreview.isPending ? 'Reading…' : 'Dry run'}
          </Button>
          {/* The import exists only after a clean preview, never beside it. */}
          {preview?.ready ? (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Import ${preview.totals.orders} package${preview.totals.orders === 1 ? '' : 's'} and issue ${preview.totals.tickets} tickets? This cannot be undone.`)) runImport.mutate();
              }}
            >
              {runImport.isPending ? 'Importing…' : `Import ${preview.totals.orders} package${preview.totals.orders === 1 ? '' : 's'}`}
            </Button>
          ) : null}
        </div>

        {preview && preview.problems.length > 0 ? (
          <div>
            <p className="text-sm font-semibold text-bad">
              {preview.problems.length} problem{preview.problems.length === 1 ? '' : 's'}. Nothing will be imported until every one is gone.
            </p>
            <ul className="mt-2 space-y-1 text-xs text-ink-700">
              {preview.problems.slice(0, 50).map((problem, index) => (
                <li key={`${problem.code}-${index}`}>
                  <span className="font-mono text-ink-500">{problem.line ? `line ${problem.line}` : problem.code}</span> · {problem.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {preview && preview.problems.length === 0 ? (
          <div>
            <p className="text-sm text-ink-700">
              {preview.totals.orders} package{preview.totals.orders === 1 ? '' : 's'} · {preview.totals.tickets} tickets across {preview.package.nights} night
              {preview.package.nights === 1 ? '' : 's'} · {dollars(preview.totals.total_cents)}
              {preview.totals.already_imported > 0 ? ` · ${preview.totals.already_imported} already imported, which this run will pass over` : ''}
            </p>
            <Table headers={['Buyer', 'Table', 'Seats', 'Paid']}>
              {preview.orders.length === 0 ? (
                <EmptyRow columns={4}>Nothing in that file belongs to this package.</EmptyRow>
              ) : (
                preview.orders.map((order) => (
                  <tr key={`${order.buyer}-${order.table}`} className="border-b border-line last:border-0">
                    <td className="px-5 py-2">
                      {order.buyer}
                      <div className="text-xs text-ink-400">{order.email}</div>
                    </td>
                    <td className="px-5 py-2">Table {order.table}</td>
                    <td className="tabular px-5 py-2">{order.seats}</td>
                    <td className="tabular px-5 py-2">
                      {/* Already here from an earlier run (D-240): the row is
                          shown because it is in the file, and marked because
                          pressing Import will pass over it. */}
                      {order.already_imported ? <Badge tone="neutral">already imported</Badge> : order.comp ? <Badge tone="accent">comp</Badge> : dollars(order.total_cents)}
                    </td>
                  </tr>
                ))
              )}
            </Table>
            {preview.dropped.length > 0 ? (
              <p className="mt-2 text-xs text-ink-500">
                {preview.dropped.length} seat{preview.dropped.length === 1 ? '' : 's'} left out because the old system says they were given back:{' '}
                {preview.dropped.slice(0, 6).map((seat) => `${seat.label} (${seat.status})`).join(', ')}
                {preview.dropped.length > 6 ? ' …' : ''}
              </p>
            ) : null}
          </div>
        ) : null}

        {result ? (
          <div>
            <p className="text-sm font-semibold text-good">
              Imported {result.totals.orders} package{result.totals.orders === 1 ? '' : 's'} · {result.totals.tickets} tickets · {dollars(result.totals.total_cents)}
            </p>
            <Table headers={['Order', 'Buyer', 'Table', 'Seats', 'Paid']}>
              {result.imported.map((order) => (
                <tr key={order.order_number} className="border-b border-line last:border-0">
                  <td className="px-5 py-2 font-mono">{order.order_number}</td>
                  <td className="px-5 py-2">{order.buyer}</td>
                  <td className="px-5 py-2">Table {order.table}</td>
                  <td className="tabular px-5 py-2">{order.seats}</td>
                  <td className="tabular px-5 py-2">{dollars(order.total_cents)}</td>
                </tr>
              ))}
            </Table>
            {result.skipped.length > 0 ? (
              <p className="mt-2 text-xs text-ink-500">
                Passed over {result.skipped.length} table{result.skipped.length === 1 ? '' : 's'} an earlier run had already imported:{' '}
                {result.skipped.slice(0, 8).map((row) => `table ${row.table}`).join(', ')}
                {result.skipped.length > 8 ? ', …' : ''}
              </p>
            ) : null}
            {result.failures.length > 0 ? (
              <p className="mt-2 text-sm text-bad">
                Stopped at {result.failures[0]!.buyer}: {result.failures[0]!.message}. The packages above are whole orders and were kept.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
